import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Button,
  Chip,
  Box,
  Typography,
  Paper, Stack,
  Pagination,
  PaginationItem
} from '@mui/material';
import { getUserInfo } from 'src/features/user/userState';
import { useAppSelector, useAppDispatch } from "src/hooks/hooks";
import { fetchEventCodes, updateEventCodes } from 'src/features/config/eventCodeApi';
import { toast } from 'react-toastify';
import { incrementAwaitingApiCounter, decrementAwaitingApiCounter } from 'src/features/ui/uiState';
import { getActivityLogs } from 'src/features/config/activityLogApi';
import { convertUnixToPstFormat } from 'src/utils/core';
import Tooltip from '@mui/material/Tooltip';
import InfoIcon from '@mui/icons-material/Info';

const EventConfig = () => {
  const dispatch = useAppDispatch();
  const eventStatus = 'Alert';
  const [eventCodes, setEventCodes] = useState([]);
  const [inputCode, setInputCode] = useState('');
  const [inputDescription, setInputDescription] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const userInfo = useAppSelector(getUserInfo);
  const token = userInfo?.accessToken;
  const activityLogsRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1); // API page number
  const [totalCount, setTotalCount] = useState(0); // Total logs from API
  const [pageSize, setPageSize] = useState(10); // Default from API response
  const [focused, setFocused] = useState(false);
  const totalPages = Math.ceil(totalCount / pageSize);
  const uiState = useAppSelector((state) => state.ui);
  const loadEventCodes = async () => {
    if (!token) return;
    // setLoading(true);
    try {
      dispatch(incrementAwaitingApiCounter());
      const response = await fetchEventCodes(eventStatus, token);
      if (response?.usps_event_codes) {
        setEventCodes(response.usps_event_codes);
      }
    } catch (error) {
      console.error('Error fetching event codes:', error);
      setSubmitStatus('Error loading event codes');
    } finally {
      // setLoading(false);
      dispatch(decrementAwaitingApiCounter());
    }
  };

  const loadActivityLogs = async () => {
    if (!token) return;
    setIsLoadingLogs(true);
    try {
      dispatch(incrementAwaitingApiCounter());
      const response = await getActivityLogs({ current_page: currentPage, page_size: pageSize, token });
      setActivityLogs(response.rows || []); // Set only the rows array
      setTotalCount(Number(response.total_count || 0));
      setPageSize(Number(response.page_size || 10)); // Update from API
      setCurrentPage(Number(response.current_page || 1)); // Sync with API
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Failed to load activity logs', {
        autoClose: 3000,
        theme: 'colored',
      });
      setActivityLogs([]); // Fallback to empty array on error
    } finally {
      dispatch(decrementAwaitingApiCounter());
      setIsLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadEventCodes();
    loadActivityLogs();
  }, [token, eventStatus, currentPage, dispatch]);

  const errorMessages = {
    "EMPTY": "Event code cannot be empty",
    "ONLY_SPACES": "Event code cannot contain only spaces",
    "TOO_MANY_CHARACTERS": "Event code cannot contain more than 2 characters",
    "NO_DESCRIPTION": "Description is required for each event code"
  };

  const defaultToastOptions = {
    autoClose: 1500,
    theme: 'colored',
  };

  const handleAddCode = (e) => {
    if (e.key === 'Enter') {
      let trimmedCode = inputCode.trim();
      const trimmedDescription = inputDescription.trim();
      if (inputCode === '') {
        toast.error(errorMessages.EMPTY, defaultToastOptions);
        return;
      }
      if (!trimmedCode) {
        toast.error(errorMessages.ONLY_SPACES, defaultToastOptions);
        setInputCode('');
        return;
      }
      if (trimmedCode.length > 2) {
        toast.error(errorMessages.TOO_MANY_CHARACTERS, defaultToastOptions);
        setInputCode('');
        return;
      }
      if (!trimmedDescription) {
        toast.error(errorMessages.NO_DESCRIPTION, defaultToastOptions);
        return;
      }
      trimmedCode = trimmedCode.toUpperCase();
      if (eventCodes.some(item => item.code === trimmedCode)) {
        toast.error(`Event code "${trimmedCode}" already exists`, defaultToastOptions);
        setInputCode('');
        setInputDescription('');
        return;
      }
      setEventCodes([...eventCodes, { code: trimmedCode, description: trimmedDescription }]);
      setInputCode('');
      setInputDescription('');
    }
  };

  const handleDeleteCode = (codeToDelete) => {
    // setEventCodes(eventCodes.filter((code) => code !== codeToDelete));
    setEventCodes(eventCodes.filter((item) => item.code !== codeToDelete));
  };

  const handleSubmit = async () => {
    const payload = {
      event_status: eventStatus,
      event_codes: eventCodes
    };
    try {
      setSubmitStatus('Submitting...');
      dispatch(incrementAwaitingApiCounter());
      const response = await updateEventCodes(payload, token);
      if (response.status === 200) {
        toast.success(response?.data?.message || "Successfully updated the event codes!", {
          autoClose: 3000,
          theme: 'colored'
        });
        setSubmitStatus('Success: Event codes updated successfully');
        await loadEventCodes();
        await loadActivityLogs();
        setSubmitStatus(null);
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.message || 'Something went wrong';
      toast.error(errorMessage, {
        autoClose: 3000,
        theme: 'colored',
      });
      setSubmitStatus('Error: Failed to update event codes');
      console.error('API Error:', error);
      const match = errorMessage.match(/event_code: (\w+)/);
      if (match && match[1]) {
        const invalidCode = match[1];
        setEventCodes(eventCodes.filter(item => item.code !== invalidCode));
      }
      setTimeout(() => setSubmitStatus(null), 1500);
    } finally {
      dispatch(decrementAwaitingApiCounter());
    }
  };

  return (
    <Box sx={{ maxWidth: 600, margin: 'auto', padding: 3 }}>

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>

        <Typography sx={{ color: "common.black" }} variant="h5" gutterBottom>
          Event Configuration
        </Typography>

        <Tooltip title="View descriptions for all event codes" arrow>
          <Button
            variant="contained"
            color="primary"
            href="https://postalpro.usps.com/product-tracking-and-reporting/scan-events-descriptions"
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<InfoIcon />}
            sx={{
              textTransform: 'none',
              px: 1.8,
              py: 1,
            }}
          >
            Code Guide
          </Button>
        </Tooltip>
      </Box>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Event Status</InputLabel>
        <Select
          sx={{ color: "common.black" }}
          value={eventStatus}
          label="Event Status"
          readOnly
        >
          <MenuItem sx={{ color: "common.black" }} value="Alert">Alert</MenuItem>
        </Select>
      </FormControl>

      <Box sx={{ mb: 2 }}>
        <TextField
          sx={{ "& .MuiInputBase-input": { color: "black" } }}
          fullWidth
          label="Event Codes (Press Enter to add)"
          value={inputCode}
          placeholder='Enter event code'
          onChange={(e) => setInputCode(e.target.value)}
          onKeyPress={handleAddCode}
          helperText="Current codes are displayed below as chips"
          disabled={uiState.awaitingApis > 0 || submitStatus === 'Submitting...'}
        />

        <TextField
          sx={{ "& .MuiInputBase-input": { color: "black" }, mt: 2 }}
          fullWidth
          label={
            <span>
              Description {focused && <span style={{ color: 'red', marginLeft: '2px' }}>*</span>}
            </span>
          }
          placeholder='Enter description'
          value={inputDescription}
          onChange={(e) => setInputDescription(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyPress={handleAddCode}
          helperText="Both code and description are required"
          disabled={uiState.awaitingApis > 0 || submitStatus === 'Submitting...'}
        />

        <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {uiState.awaitingApis > 0 ? (
            <Typography>Loading event codes...</Typography>
          ) : eventCodes.length > 0 ? (
            eventCodes.map((item) => (
              <Tooltip key={item.code} title={item.description} arrow>
                <Chip
                  label={item.code}
                  onDelete={() => handleDeleteCode(item.code)}
                  color="primary"
                  disabled={submitStatus === 'Submitting...'}
                />
              </Tooltip>
            ))
          ) : (
            <Typography>No event codes available</Typography>
          )}
        </Box>

      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        fullWidth
        disabled={submitStatus === 'Submitting...' || uiState.awaitingApis > 0}
      >
        {submitStatus === 'Submitting...' ? 'Submitting...' : 'Submit'}
      </Button>

      {submitStatus && submitStatus !== 'Submitting...' && (
        <Typography
          sx={{ mt: 2 }}
          color={submitStatus.includes('Success') ? 'success.main' : 'error.main'}
        >
          {submitStatus}
        </Typography>
      )}

      {/* Activity Logs Section */}

      <Box sx={{ mt: 4 }} ref={activityLogsRef}>
        <Typography
          sx={{
            color: "common.black",
            fontWeight: 'bold',
            letterSpacing: 0.5
          }}
          variant="h6"
          gutterBottom
        >
          Activity Logs
        </Typography>
        <Paper
          elevation={3}
          sx={{
            maxHeight: 400,
            overflowY: 'auto',
            borderRadius: 2,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
            p: 2,
            border: '1px solid',
            borderColor: 'grey.200',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'primary.main',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'grey.200',
            },
          }}
        >
          {isLoadingLogs ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2 }}
            >
              Loading activity logs...
            </Typography>
          ) : activityLogs.length > 0 ? (
            <Stack spacing={1}>
              {activityLogs.map((log, index) => (
                <Box
                  key={index}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'white',
                    p: 1.5,
                    borderRadius: 1.5,
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 12px rgba(0, 0, 0, 0.15)',
                      bgcolor: 'grey.50',
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      flexGrow: 1,
                      color: 'common.black',
                      fontWeight: 'medium',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Chip
                      label={userInfo?.user?.user_id === log.user_id ? `${log.user_name} (You)` : log.user_name}
                      size="small"
                      sx={{
                        mr: 1.5,
                        bgcolor: 'primary.main',
                        color: 'white',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                      }}
                    />
                    {log.activity_text || 'No activity text'}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'grey.700',
                      ml: 2,
                      whiteSpace: 'nowrap',
                      bgcolor: 'grey.100',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      fontWeight: 'medium',
                    }}
                  >
                    {convertUnixToPstFormat(log.activity_date)}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2, fontStyle: 'italic' }}
            >
              No activity logs available
            </Typography>
          )}
        </Paper>
      </Box>

      {totalCount > 0 && !isLoadingLogs && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 2, gap: 1 }}>
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(event, newPage) => {
              setCurrentPage(newPage);
              setTimeout(() => {
                if (activityLogsRef.current) {
                  activityLogsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }, 500);
            }}
            variant="outlined"
            shape="rounded"
            color="primary"
            renderItem={(item) => {
              const { type, page: itemPage } = item;
              const maxPagesToShow = 5; // Show 5 page numbers at a time
              const halfRange = Number(Math.floor(maxPagesToShow / 2));
              // Calculate the range of pages to show
              let startPage = Number(Math.max(1, currentPage - halfRange));
              let endPage = Number(Math.min(totalPages, startPage + maxPagesToShow - 1));

              // Adjust startPage if we're near the end
              if (endPage - startPage + 1 < maxPagesToShow) {
                startPage = Math.max(1, endPage - maxPagesToShow + 1);
              }

              // Show the item if it's within the range, or if it's an ellipsis, Previous, or Next
              if (
                type === 'previous' ||
                type === 'next' ||
                (type === 'page' && itemPage >= startPage && itemPage <= endPage) ||
                (type === 'start-ellipsis' && startPage > 2) ||
                (type === 'end-ellipsis' && endPage < totalPages - 1)
              ) {
                return (
                  <PaginationItem
                    {...item}
                    sx={{
                      // General page button styles
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                      color: 'common.black',
                      border: '1px solid',
                      borderColor: 'primary.main',
                      backgroundColor: 'transparent',
                      minWidth: '32px',
                      height: '32px',
                      '&:hover': {
                        backgroundColor: 'primary.light',
                      },

                      // Active page button (set background to white instead of shadow)
                      '&.MuiPaginationItem-page.Mui-selected': {
                        backgroundColor: 'white !important',  // Set explicit white background
                        color: 'primary.main !important',     // Change text color for visibility
                        fontWeight: 'bold',
                        borderColor: 'primary.main !important',
                        transform: 'scale(1.1)',
                        transition: 'all 0.2s ease-in-out',
                      },

                      // Styles for Previous/Next buttons
                      ...(type === 'previous' || type === 'next'
                        ? {
                          border: '1px solid',
                          borderColor: 'primary.main',
                          backgroundColor: 'transparent',
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                          },
                          '&.Mui-disabled': {
                            borderColor: 'grey.500',
                            color: 'grey.500',
                            backgroundColor: 'transparent',
                          },
                        }
                        : {}),

                      // Fix for ellipsis (Three Dots)
                      ...(type === 'start-ellipsis' || type === 'end-ellipsis'
                        ? {
                          border: '1px solid', // Add border to make it look like other buttons
                          borderColor: 'primary.main', // Match page button style
                          backgroundColor: 'transparent',
                          color: 'black !important', // Make it visible
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '32px',
                          height: '32px'
                        }
                        : {}),
                    }}
                  />
                );
              }
              return null; // Hide pages outside the range
            }}
            sx={{
              '& .MuiPagination-ul': {
                flexWrap: 'nowrap',
                gap: '4px',
              },
            }}
          />
          <Typography variant="body2" sx={{ color: "common.black" }}>
            Page {currentPage} of {totalPages} ({totalCount} records)
          </Typography>
        </Box>
      )}


    </Box>
  );
};

export default EventConfig;
